import { useState, useEffect } from 'react';
import { X, MessageSquare, Send, History, Lightbulb, Coins, AlertTriangle } from 'lucide-react';
import Button from '@components/common/Button';
import toast from 'react-hot-toast';
import mentorService from '@services/mentor.service';

const AskQuestionModal = ({ mentor, isOpen, onClose, onSubmit, onHistoryClick }) => {
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [creditBalance, setCreditBalance] = useState(null);
  const [creditPreview, setCreditPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchCreditBalance();
    }
  }, [isOpen]);

  useEffect(() => {
    if (question.trim().length > 10) {
      const timer = setTimeout(() => {
        previewCredits();
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setCreditPreview(null);
    }
  }, [question]);

  const fetchCreditBalance = async () => {
    try {
      const response = await mentorService.getUserCredits();
      if (response.success) {
        setCreditBalance(response?.data?.data);
      }
    } catch (error) {
      console.error('Failed to fetch credit balance:', error);
    }
  };

  const previewCredits = async () => {
    if (!question.trim()) return;
    
    try {
      setPreviewLoading(true);
      const response = await mentorService.previewQuestionCredits({ question: question.trim() });
      if (response.success) {
        setCreditPreview(response?.data?.data);
      }
    } catch (error) {
      console.error('Failed to preview credits:', error);
    } finally {
      setPreviewLoading(false);
    }
  };

  if (!isOpen || !mentor) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!question.trim()) {
      toast.error('Please enter your question');
      return;
    }

    setLoading(true);
    try {
      await onSubmit({
        mentorId: mentor.id,
        question: question.trim()
      });
      setQuestion('');
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Failed to send question');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/50 backdrop-blur-sm overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full my-auto border border-white/20 max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="relative bg-gradient-to-br from-primary-700 via-primary-800 to-secondary-600 p-4 sm:p-6 flex-shrink-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.22),transparent_34%)]"></div>
          <div className="relative flex items-start justify-between gap-3 mb-4">
            <div className="flex items-start gap-3 min-w-0">
              <div className="w-10 h-10 sm:w-11 sm:h-11 bg-white/15 rounded-xl flex items-center justify-center border border-white/20 flex-shrink-0">
                <MessageSquare className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg sm:text-xl font-bold text-white">Ask Your Career Question</h2>
                <p className="text-xs text-white/75 mt-1">
                  Share your full situation so the mentor can guide you properly.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {onHistoryClick && (
                <button
                  type="button"
                  onClick={onHistoryClick}
                  className="h-9 px-3 flex items-center gap-2 rounded-full bg-white/15 hover:bg-white/25 text-white text-xs sm:text-sm font-semibold border border-white/20 transition-colors"
                >
                  <History className="w-4 h-4" />
                  <span className="hidden sm:inline">History</span>
                </button>
              )}
              <button
                onClick={onClose}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>

          {/* Mentor Info */}
          <div className="relative flex items-center gap-3 bg-white/10 rounded-xl p-3 border border-white/15">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <span className="text-base font-bold text-white">
                {mentor.firstName?.charAt(0) || mentor.name?.charAt(0) || 'M'}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-white font-semibold text-sm sm:text-base truncate">
                {mentor.firstName && mentor.lastName 
                  ? `${mentor.firstName} ${mentor.lastName}` 
                  : mentor.name || 'Mentor'}
              </h3>
              <p className="text-white/80 text-xs sm:text-sm truncate">
                {mentor.currentPosition || mentor.expertise?.[0]?.jobTitle || mentor.field || 'Expert Mentor'}
              </p>
            </div>
          </div>

          {/* Credit Balance */}
          {creditBalance && (
            <div className="relative flex items-center justify-between bg-white/10 rounded-xl p-3 border border-white/15 mt-3">
              <div className="flex items-center gap-2">
                <Coins className="w-5 h-5 text-yellow-300" />
                <span className="text-white text-sm font-medium">Available Credits</span>
              </div>
              <span className="text-white text-lg font-bold">{creditBalance.totalCredits || 0}</span>
            </div>
          )}
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-4 sm:p-6 bg-gradient-to-b from-white to-gray-50 overflow-y-auto flex-1">
          <div className="mb-4">
            <div className="flex items-center justify-between gap-3 mb-2">
              <label className="block text-sm font-semibold text-primary-700">
                Write your detailed question
              </label>
              <span className="text-xs font-medium text-gray-500">
                {question.length} characters
              </span>
            </div>
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder={`Example: I am in 12th standard and confused about choosing between engineering, design, or commerce. Which career path should I choose based on my interests and future scope?\n\nExample: I recently completed BCA and want to start my career in IT. Which skills, technologies, or job roles should I focus on to get a good opportunity?\n\n`}
              className="w-full min-h-[160px] sm:min-h-[180px] px-3 sm:px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-secondary-100 focus:border-secondary-500 resize-none text-sm leading-relaxed shadow-sm bg-white placeholder:text-gray-400 placeholder:text-xs sm:placeholder:text-sm"
              disabled={loading}
            />
          </div>

          {/* Credit Preview */}
          {creditPreview && (
            <div className={`rounded-xl p-3 sm:p-4 mb-4 flex gap-3 border ${
              creditPreview.canAfford 
                ? 'bg-green-50 border-green-200' 
                : 'bg-red-50 border-red-200'
            }`}>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm ${
                creditPreview.canAfford ? 'bg-green-100' : 'bg-red-100'
              }`}>
                {creditPreview.canAfford ? (
                  <Coins className="w-4 h-4 text-green-600" />
                ) : (
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                )}
              </div>
              <div className="flex-1">
                <p className={`text-xs sm:text-sm font-semibold mb-1 ${
                  creditPreview.canAfford ? 'text-green-700' : 'text-red-700'
                }`}>
                  {creditPreview.canAfford 
                    ? `This question will cost ${creditPreview.requiredCredits} credits` 
                    : `Insufficient credits! Need ${creditPreview.shortfall} more credits`
                  }
                </p>
                <p className="text-xs text-gray-600">
                  Available: {creditPreview.availableCredits} credits
                  {/* {creditPreview.matchedKeywords?.length > 0 && (
                    <span className="ml-2">
                      • Matched keywords: {creditPreview.matchedKeywords.map(k => k.keyword).join(', ')}
                    </span>
                  )} */}
                </p>
              </div>
            </div>
          )}

          {/* Info Box */}
          <div className="bg-secondary-50 border border-secondary-100 rounded-xl p-3 sm:p-4 mb-4 flex gap-3">
            <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
              <Lightbulb className="w-4 h-4 text-secondary-600" />
            </div>
            <p className="text-xs sm:text-sm text-primary-700 leading-relaxed">
              <strong>Tip:</strong> Include your education, interests, confusion, goals, and available options in detail. A clear question helps the mentor give you a better answer.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col-reverse sm:flex-row gap-3">
            <Button
              type="button"
              variant="outline"
              className="sm:w-40"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              className="flex-1"
              loading={loading}
              disabled={!question.trim() || loading || (creditPreview && !creditPreview.canAfford)}
            >
              <Send className="w-4 h-4 mr-2" />
              {creditPreview && !creditPreview.canAfford ? 'Insufficient Credits' : 'Send Question'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AskQuestionModal;
